import React, { useRef, useState } from 'react'

import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import { ArrowLeft, Search } from 'lucide-react'

import { Icon, UserPreview } from 'components/UI'
import CurrencyField from 'components/UI/CurrencyField'
import { FieldLabel } from 'components/UI/FieldLabel'
import { ToggleRow } from 'components/UI/ToggleRow'
import { FIELD_HEIGHT, fieldSurfaceSx } from 'components/UI/fieldMetrics'
import { ProductCard } from 'components/productCard'
import { font, space } from 'settings/tokens'
import { glassSx } from 'utils/glass'
import { sheetStepSx } from 'utils/pageTransition'

import { ListForSale } from './ListForSale'
import { RecipientSearchPicker } from './RecipientSearchPicker'
import { SaleModeSelect, SaleMode } from './SaleModeSelect'
import { SellMethodSelect } from './SellMethodSelect'

import type { CertificateCustomer, Customer } from 'types'

/**
 * The seller's "Sell" sheet — a DEMO-DEPTH trim of the one on `agent/VERA-632-App.Sell`.
 *
 * ## What this is for
 *
 * Selling is built on 632 and is not on `main`: there is no listing endpoint, no sell offer, no
 * Stripe Connect onboarding and no escrow here. But the Sell action had to stop reading "Coming
 * soon", because an inert row says the feature does not exist when in fact it is built and
 * waiting on a merge. So this carries the SCREENS, wired to each other, with the single write at
 * the end replaced by a step that says plainly that payments are still being built.
 *
 * Everything a reader can see is 632's — `SellMethodSelect`, `ListForSale`, `SaleModeSelect` and
 * the offer form below are that branch's components and that branch's copy. Only the ending
 * differs, and it differs by saying so.
 *
 * ## What is deliberately absent, and why each one is safe to drop for a demo
 *
 *   - **Every API call.** `useCreateSellOffer`, `useSetSaleListing`, `useSaleListingVisibility`,
 *     `useSellerPayoutStatus`, `useStartSellerOnboarding` — none of those endpoints exist on
 *     `main`, so calling them would 404 in front of whoever is being shown this. This is the
 *     whole reason the file is a trim rather than a copy.
 *   - **The payout-setup gate.** It is a real precondition (D5: a verified account to ACCEPT a
 *     sale) read from `useSellerPayoutStatus`. With no endpoint there is no status to read, and
 *     inventing one would be a fabricated gate.
 *   - **The possession step and the group-child notes.** Group divisibility is its own
 *     behaviour on 632 (`GroupPossessionStep`, `utils/certificateGroup`), neither of which is on
 *     `main`. A demo of the sell path does not need the group case.
 *   - **`FieldStateSwap` / `useLingeringValue`.** Not on `main`. The recipient field cuts between
 *     its two states here instead of cross-fading. Cosmetic, and the cross-fade comes back with
 *     632.
 *
 * ## When VERA-632 lands
 *
 * **Take 632's version of this file and of `ListForSale.tsx` wholesale.** Both will conflict, and
 * in both cases this side is the lesser copy by construction — its hooks are the point of the
 * feature. Do not merge the two by hand. `CurrencyField`, `ToggleRow` and `SaleModeSelect` were
 * copied byte-for-byte and should merge clean; `SaleModeSelect` is the one exception, where the
 * `SaleMode` enum is declared locally because `@repo/types` does not carry it on `main` yet.
 *
 * Not a drawer itself. Like `GiftCertificate`, it is the CONTENT a parent passes to
 * `DrawerPopup`'s `child`.
 */

const PRICE_ERROR = 'The price must be greater than $0. Please enter a valid amount.'

type SellStep = 'method' | 'list' | 'form' | 'mode' | 'unavailable'

interface SellCertificateProps {
  certificate: CertificateCustomer
  /** Close the whole sheet. The parent owns the drawer. */
  onClose: () => void
}

export const SellCertificate = ({ certificate, onClose }: SellCertificateProps) => {
  const theme = useTheme()

  const [step, setStep] = useState<SellStep>('method')
  const [recipient, setRecipient] = useState<Customer | null>(null)
  const [openRecipientSearch, setOpenRecipientSearch] = useState(false)
  const [price, setPrice] = useState<number | undefined>(undefined)
  const [showOwnership, setShowOwnership] = useState(false)
  const [saleMode, setSaleMode] = useState<SaleMode | null>(null)
  /** Which path the seller took, so the closing step can name it. */
  const [tookListPath, setTookListPath] = useState(false)

  /*
    Has the seller PRESSED with a price the server would refuse? Gates the DISPLAY of the error
    only — see the long note in `ListForSale`, which 632 wrote for the same control. The press is
    the only unambiguous "I am done" signal, and the price is deliberately out of the button's
    disabled condition so that pressing can reveal the message.
  */
  const [priceSubmitted, setPriceSubmitted] = useState(false)

  const priceInvalid = price === undefined || price <= 0
  const showPriceError = priceSubmitted && priceInvalid

  /*
    Which way the next step slides in from. 632 derives this the same way: the renderer needs to
    know whether this step is a forward navigation or a return, and a ref is what survives the
    render that changes `step`.
  */
  const prevStepRef = useRef<SellStep>(step)
  const stepDirRef = useRef<'left' | 'right' | null>(null)
  const ORDER: SellStep[] = ['method', 'form', 'mode', 'unavailable']
  if (prevStepRef.current !== step) {
    const from = ORDER.indexOf(prevStepRef.current)
    const to = ORDER.indexOf(step)
    stepDirRef.current = from === -1 || to === -1 || to > from ? 'left' : 'right'
    prevStepRef.current = step
  }
  const stepSx = sheetStepSx(stepDirRef.current)

  /** A back arrow, not a second Cancel (§52) — a second dismissal competing with the sheet's own X is the ambiguity stacking drawers creates. */
  const back = (to: SellStep) => (
    <Stack
      direction='row'
      alignItems='center'
      onClick={() => setStep(to)}
      role='button'
      tabIndex={0}
      sx={{ gap: `${space.sm}px`, cursor: 'pointer', marginBottom: `${space.lg}px` }}
    >
      <ArrowLeft size={18} strokeWidth={1.5} />
      <Typography sx={{ fontSize: `${font.label}px` }}>Back</Typography>
    </Stack>
  )

  const renderStep = () => {
    if (step === 'method') {
      return (
        <SellMethodSelect
          onSelect={method => {
            setTookListPath(method === 'list')
            setStep(method === 'list' ? 'list' : 'form')
          }}
        />
      )
    }

    if (step === 'list') {
      return (
        <ListForSale
          certificate={certificate}
          /*
            Always false, and `currentPrice` always undefined: a listing is a server record and
            `main` has no table for one, so nothing here can already be listed. On 632 both come
            off the certificate payload. The consequence is that the "Update price" and "Remove
            listing" affordances never render in the demo, which is correct — there is no listing
            to update or remove.
          */
          isListed={false}
          currentPrice={undefined}
          isPubliclyVisible
          onVisibilityChange={() => undefined}
          onBack={() => setStep('method')}
          onSubmit={() => setStep('unavailable')}
        />
      )
    }

    if (step === 'mode') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '360px' }}>
          {back('form')}
          <SaleModeSelect
            onSelect={mode => {
              setSaleMode(mode)
              setStep('unavailable')
            }}
          />
        </Box>
      )
    }

    if (step === 'unavailable') {
      /*
        The one screen that is NOT 632's.

        It stands where a write would be — `POST /sell/customer/offer` on the P2P path,
        `PUT /sell/listing` on the listing path. It names the specific thing that has not
        happened ("no listing was created" / "nobody was charged"), because a bare "coming soon"
        at the END of a flow someone just filled in reads as the flow having failed. §43's
        argument for a confirmation applies in reverse here: the screen's job is to state the
        consequence, and the consequence is that there isn't one.
      */
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '240px' }}>
          {back(tookListPath ? 'list' : 'mode')}
          <Stack direction='column' spacing={3} sx={{ flex: 1, pb: 2 }}>
            <Box>
              <ProductCard product={certificate.product as any} bare />
            </Box>
            <Box>
              <Typography sx={{ fontSize: `${font.heading}px`, marginBottom: `${space.sm}px` }}>
                Not ready yet
              </Typography>
              <Typography
                sx={{ fontSize: `${font.label}px`, color: 'text.secondary', lineHeight: 1.5 }}
              >
                {tookListPath
                  ? 'Payments are still being built, so no listing was created and your certificate is unchanged.'
                  : 'Payments and escrow are still being built, so nothing was sent, nobody was charged and your certificate is unchanged.'}
              </Typography>
            </Box>
            {/*
              What the seller just filled in, read back to them.

              Not decoration: it is the evidence that the screens above did capture their input,
              which is the difference between "this is unfinished" and "this is broken". On 632
              these same values go to the server.
            */}
            <Box>
              <Typography
                sx={{
                  fontSize: `${font.label}px`,
                  color: 'text.secondary',
                  lineHeight: 1.5,
                }}
              >
                {tookListPath
                  ? 'What you entered: an asking price.'
                  : `What you entered: ${recipient ? `a sale to ${recipient.first_name ?? 'the buyer'}` : 'a buyer'}${
                      price !== undefined ? ` at $${price}` : ''
                    }${saleMode ? `, ${saleMode === SaleMode.InPerson ? 'handed over in person' : 'shipped'}` : ''}.`}
              </Typography>
            </Box>
          </Stack>
          <Box
            sx={t => ({ position: 'sticky', bottom: 0, zIndex: 2, ...glassSx(t), pt: 2, pb: 1 })}
          >
            <Button fullWidth variant='contained' onClick={onClose}>
              Done
            </Button>
          </Box>
        </Box>
      )
    }

    // step === 'form' — the P2P offer form.
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '500px' }}>
        {back('method')}

        {/* One Stack owns every gap. Each section is a bare Box and a DIRECT child — a local
            `mb` on a direct child is silently killed by MUI's Stack spacing rule. */}
        <Stack direction='column' spacing={3} sx={{ flex: 1, pb: 2 }}>
          <Box>
            <ProductCard product={certificate.product as any} bare />
          </Box>

          <Box>
            {/* "Recipient", not the Figma's "To" — VERA-304 round-4 verify feedback renamed this
                in the gift flow, and shipping "To" here beside Gift's "Recipient" would
                reintroduce the inconsistency it fixed. */}
            <FieldLabel label='Recipient' />
            {recipient ? (
              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <UserPreview user={recipient} avatarUrl={recipient.avatar} />
                {/* X, not a trash can: clearing a chosen recipient is set-removal, not the
                    destruction of a record (§38). Same treatment as the gift flow. */}
                <Icon
                  name='close'
                  sx={(t: any) => ({ color: t.palette.grey[600], cursor: 'pointer' })}
                  onClick={() => setRecipient(null)}
                />
              </Stack>
            ) : (
              <Stack
                direction='row'
                alignItems='center'
                onClick={() => setOpenRecipientSearch(true)}
                sx={{
                  ...fieldSurfaceSx(theme),
                  height: FIELD_HEIGHT,
                  paddingX: `${space.md}px`,
                  gap: `${space.sm}px`,
                  cursor: 'pointer',
                }}
              >
                <Search size={16} strokeWidth={1.5} color={theme.palette.text.disabled} />
                <Typography sx={{ fontSize: `${font.body}px`, color: 'text.disabled' }}>
                  Search by name or @handle
                </Typography>
              </Stack>
            )}
          </Box>

          <Box>
            <CurrencyField
              label='Price'
              value={price}
              onChange={setPrice}
              error={showPriceError ? PRICE_ERROR : undefined}
            />
          </Box>

          <Box>
            {/* The label is 632's, unchanged and deliberately so: "Show ownership" is the app's
                single name for this setting — the Share screen's view settings use it verbatim
                (§44). */}
            <ToggleRow
              label='Show ownership'
              description='Your identity will be publicly available even after you transfer ownership. This can be changed anytime.'
              checked={showOwnership}
              onChange={setShowOwnership}
            />
          </Box>
        </Stack>

        {/* Pinned OUTSIDE the Stack, matching GiftCertificate — that file records the measurement
            that forced it: the button otherwise sat at 771–815px in an 812px viewport. */}
        <Box sx={t => ({ position: 'sticky', bottom: 0, zIndex: 2, ...glassSx(t), pt: 2, pb: 1 })}>
          <Button
            fullWidth
            variant='contained'
            /*
              Gates on the RECIPIENT, not on the price. 632 removed the price from this condition
              deliberately: with it in, the button was dead in exactly the state the price error
              describes, so the error could never be reached and the seller got a control that
              refused to act and refused to explain.
            */
            disabled={!recipient}
            onClick={() => {
              setPriceSubmitted(true)
              if (priceInvalid || !recipient) return
              setStep('mode')
            }}
          >
            Sell
          </Button>
        </Box>

        <RecipientSearchPicker
          open={openRecipientSearch}
          onClose={() => setOpenRecipientSearch(false)}
          onSelectRecipient={setRecipient}
          // Not the default "Gift to" — this is a sale, and the picker is shared.
          title='Sell to'
        />
      </Box>
    )
  }

  /*
    `key={step}` is load-bearing: without it React reuses this Box across steps, and a CSS
    animation only runs on mount — so the slide would play once and never again.
  */
  return (
    <Box key={step} sx={stepSx}>
      {renderStep()}
    </Box>
  )
}
